/**
 * Full-page variation picker — shown when a modifier option has
 * multiple variations (e.g. size, temperature).
 */
import { useTranslation } from 'react-i18next'
import { ChevronLeft } from 'lucide-react'
import { Button } from '@/components/ui'
import { formatPrice } from '@/utils/formatting'
import type { ModifierOption, ModifierOptionVariation } from '@/types/modifier'

interface VariationPickerPageProps {
  option: ModifierOption
  groupName: string
  selectedVariation: ModifierOptionVariation | null
  onSelect: (variation: ModifierOptionVariation) => void
  onBack: () => void
  currency: string
}

export function VariationPickerPage({
  option,
  selectedVariation,
  onSelect,
  onBack,
  currency,
}: VariationPickerPageProps) {
  const { t } = useTranslation()
  const variations = option.variationGroup?.variations || []
  const defaultVariation = variations.find((v) => v.is_default)
  const defaultPrice = defaultVariation?.price

  return (
    <div className="px-5 sm:px-6 pt-16 pb-4">
      <div className="space-y-4">
        {/* Back Header */}
        <div className="flex items-center gap-3 pb-4 border-b">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="w-11 h-11"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h3 className="font-semibold text-lg">{option.name}</h3>
            <p className="text-sm text-muted-foreground">
              {t('common:select', { name: option.variationGroup?.name })}
            </p>
          </div>
        </div>

        {/* Variations List */}
        <div className="space-y-2">
          {[...variations]
            .sort((a, b) => a.sort_order - b.sort_order)
            .map((variation) => {
              const isSelected = selectedVariation?.id === variation.id

              let priceLabel = ''
              if (variation.price !== undefined && defaultPrice !== undefined) {
                const diff = variation.price - defaultPrice
                if (diff > 0) {
                  priceLabel = `+${formatPrice(diff, currency, false)}`
                } else if (diff < 0) {
                  priceLabel = `-${formatPrice(Math.abs(diff), currency, false)}`
                }
              }

              return (
                <Button
                  key={variation.id}
                  variant="outline"
                  className={`w-full flex items-center justify-between p-4 h-auto rounded-xl border-2 text-left ${
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50 hover:bg-muted/50'
                  }`}
                  onClick={() => onSelect(variation)}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                        isSelected ? 'border-primary' : 'border-muted-foreground/30'
                      }`}
                    >
                      {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
                    </div>
                    <div>
                      <p className={`font-medium ${isSelected ? 'text-primary' : ''}`}>
                        {variation.name}
                      </p>
                      {priceLabel && (
                        <p className="text-sm text-muted-foreground font-normal">{priceLabel}</p>
                      )}
                    </div>
                  </div>
                  {variation.price !== undefined ? (
                    <span className="font-semibold text-sm">{formatPrice(variation.price, currency, false)}</span>
                  ) : (
                    <span className="text-xs text-muted-foreground italic font-normal">{t('common:noPrice')}</span>
                  )}
                </Button>
              )
            })}
        </div>
      </div>
    </div>
  )
}
